import React, { useRef, useEffect, useState } from 'react'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { RadarChart } from 'echarts/charts'
import {
  GridComponent,
  TitleComponent,
  TooltipComponent,
  RadarComponent,
  GraphicComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { RadarContainer } from './Radar.styles'
import { RadarProps } from './Radar.types'

echarts.use([
  RadarChart,
  GridComponent,
  TitleComponent,
  TooltipComponent,
  RadarComponent,
  GraphicComponent,
  CanvasRenderer,
])

export const Radar: React.FC<RadarProps> = ({
  indicators,
  values,
  centerValue,
  color = '#4A5BF6',
  size = 200,
  className,
}) => {
  const chartRef = useRef<ReactEChartsCore>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    const el = containerRef.current

    if (!el) return

    if (el.clientWidth > 0 && el.clientHeight > 0) {
      setIsReady(true)
      return
    }

    const observer = new window.ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          setIsReady(true)
          observer.disconnect()
        }
      }
    })

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  const option: echarts.EChartsCoreOption = {
    radar: {
      indicator: indicators,
      shape: 'polygon',
      splitNumber: 5,
      center: ['50%', '50%'],
      radius: '60%',
      axisName: {
        color: '#999999',
        fontSize: Math.max(8, Math.round(size * 0.032)),
        fontFamily: 'Poppins, Inter, sans-serif',
      },
      splitLine: {
        lineStyle: {
          color: '#E5E5E5',
          width: 1,
        },
      },
      splitArea: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: '#E5E5E5',
          width: 1,
        },
      },
    },
    series: [
      {
        type: 'radar',
        data: [
          {
            value: values,
            areaStyle: {
              color: color,
            },
            lineStyle: {
              color: color,
              width: 2,
            },
            itemStyle: {
              color: color,
              borderWidth: 0,
            },
          },
        ],
        symbol: 'none',
      },
    ],
    graphic:
      centerValue !== undefined
        ? [
            {
              type: 'text',
              left: 'center',
              top: 'center',
              z: 100,
              style: {
                text: centerValue.toString(),
                font: `600 ${Math.max(14, Math.round(size * 0.07))}px Poppins`,
                fill: '#1A1A1A',
                align: 'center',
                verticalAlign: 'middle',
              },
            },
          ]
        : [],
  }

  useEffect(() => {
    if (chartRef.current) {
      const instance = chartRef.current.getEchartsInstance()
      instance.resize()
    }
  }, [size])

  return (
    <RadarContainer ref={containerRef} size={size} className={className}>
      {isReady && (
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={option}
          notMerge={true}
          lazyUpdate={true}
          style={{ height: '100%', width: '100%' }}
        />
      )}
    </RadarContainer>
  )
}
